import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Layout from '../components/Layout';
import { blogPosts, blogCategories } from '../data/blogPosts';
import { 
  Mail, QrCode, Layout as LayoutIcon, FileText, CheckCircle,
  BookOpen, Users, ArrowRight, Zap, Target, Smartphone, Sparkles,
  PenTool, Palette, Star, ChevronDown, ChevronUp,
  Briefcase, Heart, Building, TrendingUp, CreditCard
} from 'lucide-react';

// Resource data - only completed and working tools
const freeTools = [
  {
    title: 'Email Signature Generator',
    description: 'Create professional email signatures with custom branding, social links, and contact info.',
    url: '/email-signature-generator',
    icon: Mail,
    color: 'from-blue-500 to-cyan-500',
    badge: 'Popular'
  },
  {
    title: 'QR Code Generator',
    description: 'Generate custom QR codes for websites, contact info, WiFi, and more.',
    url: '/free-qr-code-generator',
    icon: QrCode,
    color: 'from-green-500 to-emerald-500',
    badge: 'Free'
  },
  {
    title: 'Meeting Background Generator',
    description: 'Create professional virtual meeting backgrounds with your branding.',
    url: '/meeting-themes-generator',
    icon: LayoutIcon,
    color: 'from-teal-500 to-cyan-500',
    badge: 'Free'
  }
];

const guides = [
  {
    title: 'Best Networking Tools for Professionals',
    description: 'Discover the top digital networking tools to grow your professional network.',
    url: '/blog/best-networking-tools',
    icon: Users,
    readTime: '5 min read'
  },
  {
    title: 'Complete NFC Business Card Guide',
    description: 'Everything you need to know about NFC smart business cards.',
    url: '/blog/nfc-business-card-guide',
    icon: Smartphone,
    readTime: '8 min read'
  },
  {
    title: 'Digital vs Paper Business Cards',
    description: 'Compare the pros and cons of digital and traditional paper cards.',
    url: '/blog/digital-vs-paper-business-cards',
    icon: FileText,
    readTime: '6 min read'
  },
  {
    title: 'Lead Generation for Networking',
    description: 'How to capture and convert leads from networking events.',
    url: '/blog/lead-generation-networking',
    icon: Target,
    readTime: '7 min read'
  },
  {
    title: 'How to Create a Digital Business Card',
    description: 'Step-by-step guide to creating your first digital business card.',
    url: '/blog/how-to-create-digital-business-card',
    icon: BookOpen,
    readTime: '4 min read'
  }
];

const quickLinks = [
  { title: 'Create Your Card', url: '/create', description: 'Start building your digital card' },
  { title: 'Digital Business Card', url: '/digital-business-card', description: 'Learn about digital cards' },
  { title: 'NFC Cards Shop', url: '/shop', description: 'Buy NFC business cards' },
  { title: 'Pricing', url: '/pricing', description: 'View our pricing plans' },
  { title: 'Book a Demo', url: '/book-demo', description: 'Schedule a live demo' },
  { title: 'Contact Us', url: '/contact', description: 'Get in touch with our team' }
];

// Category icon mapping for blogs
const categoryIconMap = {
  'Business': Briefcase,
  'Networking': Users,
  'Technology': Zap,
  'Etiquette': BookOpen,
  'Industry Guide': Target,
  'Tools & Reviews': PenTool,
  'Personal Branding': Star,
  'Future Trends': TrendingUp,
};

const Resources = () => {
  const [showAllBlogs, setShowAllBlogs] = useState(false);
  const [showAllDesigns, setShowAllDesigns] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const loadingTemplates = false;
  
  // Static template data for SEO - fetched from API and cached with thumbnails
  const staticTemplates = [
    { id: '1', name: 'Shop Owner Smart Card', slug: 'shop-owner-smart-card', category_slug: 'indian-sme-entrepreneurs', thumbnail: 'https://firebasestorage.googleapis.com/v0/b/connectqr-efbe9.firebasestorage.app/o/template-images%2F1772019464922-template-thumbnail.png?alt=media&token=cd168258-aced-48e7-a657-b4ae6742d47a' },
    { id: '2', name: 'Retail Business Card', slug: 'retail-business-card', category_slug: 'indian-sme-entrepreneurs', thumbnail: 'https://firebasestorage.googleapis.com/v0/b/connectqr-efbe9.firebasestorage.app/o/template-images%2F1772095787965-template-thumbnail.png?alt=media&token=157a4624-0aa6-4d94-b767-0ad1355d4199' },
    { id: '3', name: 'Export Business Card', slug: 'export-business-card', category_slug: 'indian-sme-entrepreneurs', thumbnail: 'https://firebasestorage.googleapis.com/v0/b/connectqr-efbe9.firebasestorage.app/o/template-images%2F1772095696056-template-thumbnail.png?alt=media&token=e8462de1-0ff3-4944-97b7-dfe7cd6ad73f' },
    { id: '4', name: 'Manufacturer Pro Card', slug: 'manufacturer-pro-card', category_slug: 'indian-sme-entrepreneurs', thumbnail: 'https://firebasestorage.googleapis.com/v0/b/connectqr-efbe9.firebasestorage.app/o/template-images%2F1772096507895-template-thumbnail.png?alt=media&token=aa4ef9d5-3ad3-4a0c-b7b7-c391231ec390' },
    { id: '5', name: 'Startup Founder Card', slug: 'startup-founder-card', category_slug: 'indian-sme-entrepreneurs', thumbnail: 'https://firebasestorage.googleapis.com/v0/b/connectqr-efbe9.firebasestorage.app/o/template-images%2F1772020163351-template-thumbnail.png?alt=media&token=180a6610-a0af-423b-8fc7-4a0a38a6bb6e' },
    { id: '6', name: 'Loan Specialist Card', slug: 'loan-specialist-card', category_slug: 'finance-banking', thumbnail: 'https://firebasestorage.googleapis.com/v0/b/connectqr-efbe9.firebasestorage.app/o/template-images%2F1772445347639-template-thumbnail.png?alt=media&token=5dfddbb8-a421-48a2-a29b-552cd75b3487' },
    { id: '7', name: 'Banking Executive Card', slug: 'banking-executive-card', category_slug: 'finance-banking', thumbnail: 'https://firebasestorage.googleapis.com/v0/b/connectqr-efbe9.firebasestorage.app/o/template-images%2F1772531703336-template-thumbnail.png?alt=media&token=fd87b1a3-ae20-44e3-b0d2-a2af5ee2124a' },
    { id: '8', name: 'Investment Advisor Card', slug: 'investment-advisor-card', category_slug: 'finance-banking', thumbnail: 'https://firebasestorage.googleapis.com/v0/b/connectqr-efbe9.firebasestorage.app/o/template-images%2F1772456928490-template-thumbnail.png?alt=media&token=78026757-f245-4de9-a70b-d9cdf7db6403' },
    { id: '9', name: 'Networking Pro Card', slug: 'networking-pro-card', category_slug: 'individuals', thumbnail: 'https://firebasestorage.googleapis.com/v0/b/connectqr-efbe9.firebasestorage.app/o/template-images%2F1772176541569-template-thumbnail.png?alt=media&token=b6c4fe66-1a72-4e92-b65c-d08d9d8ac8f9' },
    { id: '10', name: 'Personal Brand Elite', slug: 'personal-brand-elite', category_slug: 'individuals', thumbnail: 'https://firebasestorage.googleapis.com/v0/b/connectqr-efbe9.firebasestorage.app/o/template-images%2F1772001187233-template-thumbnail.png?alt=media&token=d6e1ee05-fb6f-4dd9-a13b-c039fa64d1e9' },
    { id: '11', name: 'Creator Studio Card', slug: 'creator-studio-card', category_slug: 'individuals', thumbnail: 'https://firebasestorage.googleapis.com/v0/b/connectqr-efbe9.firebasestorage.app/o/template-images%2F1772176626017-template-thumbnail.png?alt=media&token=61f25e7b-5a92-4dee-84d5-0d4b46f6ae0d' },
    { id: '12', name: 'Young Professional Card', slug: 'young-professional-card', category_slug: 'individuals', thumbnail: 'https://firebasestorage.googleapis.com/v0/b/connectqr-efbe9.firebasestorage.app/o/template-images%2F1772176751305-template-thumbnail.png?alt=media&token=0f4e9e3b-eb6a-47f3-b6f9-e4f50e5aa5eb' },
  ];
  
  // Use static templates for display
  const templates = staticTemplates;
  
  // Filter blogs - show first 12 initially, all when expanded
  const filteredBlogs = blogPosts.filter(post => 
    selectedCategory === 'all' || post.category.toLowerCase().replace(/ & /g, '-').replace(/ /g, '-') === selectedCategory
  );
  const displayedBlogs = showAllBlogs ? filteredBlogs : filteredBlogs.slice(0, 12);
  
  // Display templates - show first 12 initially, all when expanded
  const displayedTemplates = showAllDesigns ? templates : templates.slice(0, 12);
  
  // Structured data for CollectionPage schema
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "PiperCard Tools, Templates & Networking Resources",
    "description": "Explore free QR code generators, email signature makers, digital business card templates and professional networking tools.",
    "url": "https://pipercard.com/resources",
    "publisher": {
      "@type": "Organization",
      "name": "PiperCard",
      "url": "https://pipercard.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://pipercard.com/logo.png"
      }
    },
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": [
        ...freeTools.map((tool, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "SoftwareApplication",
            "name": tool.title,
            "description": tool.description,
            "url": `https://pipercard.com${tool.url}`,
            "applicationCategory": "BusinessApplication",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            }
          }
        }))
      ]
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>PiperCard Tools, Templates & Networking Resources</title>
        <meta 
          name="description" 
          content="Explore free QR code generators, email signature makers, digital business card templates and professional networking tools." 
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://pipercard.com/resources" />
        
        {/* Open Graph */}
        <meta property="og:title" content="PiperCard Tools, Templates & Networking Resources" />
        <meta property="og:description" content="Explore free QR code generators, email signature makers, digital business card templates and professional networking tools." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pipercard.com/resources" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="PiperCard Tools, Templates & Networking Resources" />
        <meta name="twitter:description" content="Explore free QR code generators, email signature makers, digital business card templates and professional networking tools." />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section */}
        <section className="relative py-16 sm:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 to-orange-500/5"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                Tools, Templates & Resources
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Everything you need to create stunning digital business cards, generate QR codes, 
                and grow your professional network.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Link 
                  to="/create"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-semibold transition-colors shadow-lg shadow-amber-500/25"
                >
                  <Sparkles className="w-5 h-5" />
                  Create Your Card
                </Link>
                <Link 
                  to="/shop"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white hover:bg-gray-50 text-gray-900 rounded-xl font-semibold transition-colors border border-gray-200"
                >
                  Browse Templates
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Free Tools Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8" id="tools">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900">Free Tools</h2>
                <p className="text-gray-600">Professional tools to boost your networking</p>
              </div>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {freeTools.map((tool, index) => {
                const Icon = tool.icon;
                return (
                  <Link 
                    key={index}
                    to={tool.url}
                    className="group bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-xl hover:border-amber-200 transition-all duration-300"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-12 h-12 bg-gradient-to-br ${tool.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      {tool.badge && (
                        <span className="text-xs font-medium px-2 py-1 bg-amber-100 text-amber-700 rounded-full">
                          {tool.badge}
                        </span>
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-amber-600 transition-colors">
                      {tool.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {tool.description}
                    </p>
                    <div className="mt-4 flex items-center text-amber-600 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      Try Now <ArrowRight className="w-4 h-4 ml-1" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* All Design Templates Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50" id="designs">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-violet-500 rounded-2xl flex items-center justify-center">
                <Palette className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900">All NFC Card Designs</h2>
                <p className="text-gray-600">{templates.length}+ premium NFC business card templates</p>
              </div>
            </div>
            
            {loadingTemplates ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-gray-500">Loading designs...</p>
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {displayedTemplates.map((template, index) => (
                    <Link 
                      key={template.id || index}
                      to={`/shop/${template.category_slug || 'business'}/${template.slug}`}
                      className="group bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-xl hover:border-purple-200 transition-all duration-300"
                    >
                      {(template.thumbnail || template.front_image) ? (
                        <div className="aspect-[3/2] overflow-hidden bg-gray-100">
                          <img 
                            src={template.thumbnail || template.front_image} 
                            alt={template.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                          />
                        </div>
                      ) : (
                        <div className="aspect-[3/2] bg-gradient-to-br from-purple-100 to-violet-100 flex items-center justify-center">
                          <CreditCard className="w-12 h-12 text-purple-300" />
                        </div>
                      )}
                      <div className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-medium px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full capitalize">
                            {template.category_slug?.replace(/-/g, ' ') || 'Business'}
                          </span>
                        </div>
                        <h3 className="font-semibold text-gray-900 text-sm group-hover:text-purple-600 transition-colors line-clamp-1">
                          {template.name}
                        </h3>
                        <div className="mt-2 flex items-center text-purple-600 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                          View Design <ArrowRight className="w-3 h-3 ml-1" />
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
                
                {/* Show More/Less Button */}
                {templates.length > 12 && (
                  <div className="mt-8 text-center">
                    <button
                      onClick={() => setShowAllDesigns(!showAllDesigns)}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold transition-colors"
                    >
                      {showAllDesigns ? (
                        <>
                          Show Less <ChevronUp className="w-5 h-5" />
                        </>
                      ) : (
                        <>
                          View All {templates.length} Designs <ChevronDown className="w-5 h-5" />
                        </>
                      )}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* All Blog Articles Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8" id="blogs">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">All Blog Articles</h2>
                  <p className="text-gray-600">{blogPosts.length}+ articles on networking, business cards & professional growth</p>
                </div>
              </div>
            </div>
            
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2 mb-8">
              <button
                onClick={() => { setSelectedCategory('all'); setShowAllBlogs(false); }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === 'all' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All Articles
              </button>
              {blogCategories.filter(c => c.slug !== 'all').map((category) => (
                <button
                  key={category.slug}
                  onClick={() => { setSelectedCategory(category.slug); setShowAllBlogs(false); }}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === category.slug 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {displayedBlogs.map((post, index) => {
                const Icon = categoryIconMap[post.category] || BookOpen;
                return (
                  <Link 
                    key={post.id || index}
                    to={`/blog/${post.slug}`}
                    className="group bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-xl hover:border-blue-200 transition-all duration-300"
                  >
                    {post.image && (
                      <div className="aspect-video overflow-hidden">
                        <img 
                          src={post.image} 
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                      </div>
                    )}
                    <div className="p-5">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-medium px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                          {post.category}
                        </span>
                        <span className="text-xs text-gray-500">{post.readTime}</span>
                      </div>
                      <h3 className="text-base font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed line-clamp-2">
                        {post.excerpt}
                      </p>
                      <div className="mt-3 flex items-center text-blue-600 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                        Read Article <ArrowRight className="w-4 h-4 ml-1" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
            
            {/* Show More/Less Button */}
            {filteredBlogs.length > 12 && (
              <div className="mt-8 text-center">
                <button
                  onClick={() => setShowAllBlogs(!showAllBlogs)}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors"
                >
                  {showAllBlogs ? (
                    <>
                      Show Less <ChevronUp className="w-5 h-5" />
                    </>
                  ) : (
                    <>
                      View All {filteredBlogs.length} Articles <ChevronDown className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Quick Links Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-900 to-gray-800" id="quick-links">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Quick Links</h2>
              <p className="text-gray-400">Jump to popular pages and features</p>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {quickLinks.map((link, index) => (
                <Link 
                  key={index}
                  to={link.url}
                  className="group flex items-center gap-4 p-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all"
                >
                  <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center group-hover:bg-amber-500 transition-colors">
                    <ArrowRight className="w-5 h-5 text-amber-400 group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium group-hover:text-amber-400 transition-colors">
                      {link.title}
                    </h3>
                    <p className="text-gray-400 text-sm">{link.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Ready to Create Your Digital Business Card?
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Join thousands of professionals who have upgraded their networking with PiperCard.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link 
                to="/create"
                className="inline-flex items-center gap-2 px-8 py-4 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-semibold text-lg transition-colors shadow-lg shadow-amber-500/25"
              >
                <Sparkles className="w-6 h-6" />
                Get Started Free
              </Link>
              <Link 
                to="/digital-business-card"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-xl font-semibold text-lg transition-colors"
              >
                Learn More
                <ArrowRight className="w-6 h-6" />
              </Link>
            </div>
            
            {/* Trust Indicators */}
            <div className="mt-12 flex flex-wrap items-center justify-center gap-8 text-gray-500">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span>Free to start</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span>No credit card required</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span>Premium NFC cards available</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default Resources;
